/**
 * <cart-attribution> — writes the Outbrain click id into cart.attributes so the
 * order carries the attribution (write matrix #7).
 *
 * Rebuilt from live's EdCartOutbrainCid with its R-01 defect removed. Live
 * polled for the `outbrain_cid` cookie (1s interval, 3s cutoff) and POSTed
 * /cart/update.js UNCONDITIONALLY whenever the cookie existed — one write per
 * page view, plus one per drawer re-render, forever, even when the attribute
 * already held the same value. Live's own theme contained the correct pattern
 * in a dead component (`(legacy)init-cart-attribute`: diff against current
 * attributes, then write) — that pattern is what ships here.
 *
 *  - The element renders inside the drawer form with data-current = the
 *    server-rendered cart.attributes value. Cookie present AND equal → ZERO
 *    writes, on every page, forever after the first successful write.
 *  - Cookie present and DIFFERENT (a new Outbrain click) → one write. That is
 *    a genuine state change of the attribution state, not a page-view write.
 *  - Retry is BOUNDED: 2 retries with 1s / 4s backoff, then give up silently
 *    (live's cart layer retried failures without bound; not ported).
 *  - Live's cookie polling (the cookie may be set late by the ad script) is
 *    kept: 1s interval, hard stop at 3s, cleaned up on disconnect.
 */
if (!customElements.get('cart-attribution')) {
  const RETRY_DELAYS_MS = [1000, 4000];

  class CartAttribution extends HTMLElement {
    connectedCallback() {
      this.attempted = false;
      this.checkInterval = setInterval(() => this.checkForCookie(), 1000);
      this.timeoutId = setTimeout(() => this.stopChecking(), 3000);
      this.checkForCookie();
    }

    disconnectedCallback() {
      this.stopChecking();
    }

    stopChecking() {
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    }

    checkForCookie() {
      const value = this.getCookie('outbrain_cid');
      if (!value) return;
      this.stopChecking();
      if (this.attempted) return;
      this.attempted = true;

      // The diff that live's writer lacked: identical value = no write.
      if (value === this.dataset.current) return;
      this.updateCartAttribute(value, 0);
    }

    updateCartAttribute(value, attempt) {
      fetch(`${routes.cart_update_url}`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { outbrain_cid: value } }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`cart attribute write failed (${response.status})`);
          this.dataset.current = value;
        })
        .catch((error) => {
          if (attempt < RETRY_DELAYS_MS.length) {
            setTimeout(() => this.updateCartAttribute(value, attempt + 1), RETRY_DELAYS_MS[attempt]);
          } else {
            console.error('Failed to update cart attribute:', error);
          }
        });
    }

    getCookie(name) {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.startsWith(`${name}=`)) return decodeURIComponent(cookie.substring(name.length + 1));
      }
      return null;
    }
  }

  customElements.define('cart-attribution', CartAttribution);
}
